/**
 * Oddit global interactions — static preview bundle.
 * Adds html.oddit-js, stagger indices, reveal IO, section activation, parallax.
 * Reveals use threshold 0 so “peek-a-boo” carousel slides (only a sliver visible)
 * still get oddit-is-visible on load; a post-layout flush matches the same root bounds.
 *
 * Theme editor: re-applies visibility on shopify:section:load / editor load so
 * re-rendered sections are not stuck at opacity 0 (missing oddit-section-active / oddit-is-visible).
 */
(function () {
  'use strict';

  document.documentElement.classList.add('oddit-js');

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var staticPreview = document.documentElement.classList.contains(
    'oddit-static-preview',
  );

  var sectionShellObserver = null;
  var revealObserver = null;
  var parallaxInitialized = false;
  var requestParallaxUpdate = null;

  function isThemeEditor() {
    return (
      typeof window.Shopify !== 'undefined' &&
      window.Shopify.designMode === true
    );
  }

  function initStagger(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-oddit-stagger]').forEach(function (container) {
      var idx = 0;
      Array.prototype.forEach.call(container.children, function (child) {
        if (child.classList && child.classList.contains('oddit-reveal')) {
          child.style.setProperty('--oddit-stagger-index', String(idx));
          idx += 1;
        }
      });
    });
  }

  // Native popovers don't lock background scroll. Any [popover] that opts in with
  // data-oddit-lock-scroll freezes body scroll while open (e.g. the size guide modal).
  // The toggle event doesn't bubble, so bind per element (guarded against rebinding).
  function initPopoverScrollLock(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope
      .querySelectorAll('[popover][data-oddit-lock-scroll]')
      .forEach(function (el) {
        if (el.dataset.odditScrollLockBound === 'true') return;
        el.dataset.odditScrollLockBound = 'true';
        el.addEventListener('toggle', function (e) {
          document.body.style.overflow = e.newState === 'open' ? 'hidden' : '';
        });
      });
  }

  function ensureSectionShellObserver() {
    if (staticPreview || reducedMotion.matches) {
      return null;
    }
    if (!sectionShellObserver) {
      sectionShellObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('oddit-section-active');
            }
          });
        },
        { rootMargin: '0px 0px -5% 0px', threshold: 0.01 },
      );
    }
    return sectionShellObserver;
  }

  function observeShopifySection(el) {
    if (staticPreview || reducedMotion.matches) {
      el.classList.add('oddit-section-active');
      return;
    }
    var io = ensureSectionShellObserver();
    if (io) {
      io.observe(el);
    }
  }

  /**
   * Axis-aligned intersection against the same effective root as the reveal
   * IntersectionObserver (viewport + rootMargin). Used to flush reveals after
   * layout so peek-a-boo slides are not stuck hidden for a frame.
   */
  function getRevealRootBounds() {
    var w = window.innerWidth || 0;
    var h = window.innerHeight || 0;
    var bottomInset = h * 0.08;
    return {
      top: 0,
      left: 0,
      right: w,
      bottom: h - bottomInset,
    };
  }

  function rectIntersectsRevealRoot(rect) {
    var root = getRevealRootBounds();
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.left < root.right &&
      rect.right > root.left &&
      rect.top < root.bottom &&
      rect.bottom > root.top
    );
  }

  /**
   * Mark any already-intersecting reveals visible (and unobserve). Covers
   * carousels that show part of the next slide: a small visible fraction can be
   * below the old 5% IO threshold; threshold 0 fixes IO, this catches late layout.
   */
  function flushInitiallyVisibleReveals(scope) {
    if (reducedMotion.matches || staticPreview || isThemeEditor()) {
      return;
    }
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('.oddit-reveal:not(.oddit-is-visible)').forEach(function (el) {
      if (rectIntersectsRevealRoot(el.getBoundingClientRect())) {
        el.classList.add('oddit-is-visible');
        if (revealObserver) {
          revealObserver.unobserve(el);
        }
      }
    });
  }

  function ensureRevealObserver() {
    if (reducedMotion.matches) {
      return null;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('oddit-is-visible');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        {
          root: null,
          rootMargin: '0px 0px -8% 0px',
          threshold: 0,
        },
      );
    }
    return revealObserver;
  }

  function observeReveal(el) {
    if (reducedMotion.matches) {
      el.classList.add('oddit-is-visible');
      return;
    }
    var io = ensureRevealObserver();
    if (io) {
      io.observe(el);
    }
  }

  function initShopifySections() {
    var sections = document.querySelectorAll('.shopify-section');
    if (!sections.length) {
      return;
    }

    if (staticPreview || reducedMotion.matches) {
      sections.forEach(function (el) {
        el.classList.add('oddit-section-active');
      });
      return;
    }

    var io = ensureSectionShellObserver();
    if (!io) {
      return;
    }
    sections.forEach(function (el) {
      io.observe(el);
    });
  }

  function initReveals() {
    var nodes = document.querySelectorAll('.oddit-reveal');
    if (!nodes.length) {
      return;
    }

    if (reducedMotion.matches) {
      nodes.forEach(function (el) {
        el.classList.add('oddit-is-visible');
      });
      return;
    }

    nodes.forEach(function (el) {
      observeReveal(el);
    });
  }

  function initParallax() {
    if (reducedMotion.matches) {
      return;
    }

    var ticking = false;

    function update() {
      ticking = false;
      var els = document.querySelectorAll('.oddit-parallax');
      var vh = window.innerHeight || 1;
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        var rect = el.getBoundingClientRect();
        var raw = el.getAttribute('data-oddit-parallax-speed');
        var speed = raw != null ? parseFloat(raw) : 0.15;
        if (Number.isNaN(speed)) {
          speed = 0.15;
        }
        var centerOffset = rect.top + rect.height / 2 - vh / 2;
        var relative = centerOffset / vh;
        var ty = relative * 100 * speed;
        el.style.transform = 'translate3d(0, ' + ty + 'px, 0)';
      }
    }

    function onFrame() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }

    requestParallaxUpdate = onFrame;

    if (!parallaxInitialized) {
      parallaxInitialized = true;
      window.addEventListener('scroll', onFrame, { passive: true });
      window.addEventListener('resize', onFrame, { passive: true });
    }
    onFrame();
  }

  /**
   * Theme editor: new DOM has no oddit-section-active / oddit-is-visible; apply immediately.
   * Storefront section AJAX: wire IntersectionObserver for the replaced section subtree.
   */
  function attachInteractionsToSectionRoot(sectionRoot) {
    if (!sectionRoot || !sectionRoot.querySelectorAll) {
      return;
    }

    if (staticPreview || reducedMotion.matches) {
      initStagger(sectionRoot);
      if (sectionRoot.classList && sectionRoot.classList.contains('shopify-section')) {
        sectionRoot.classList.add('oddit-section-active');
      }
      sectionRoot.querySelectorAll('.oddit-reveal').forEach(function (el) {
        el.classList.add('oddit-is-visible');
      });
      return;
    }

    if (isThemeEditor()) {
      initStagger(sectionRoot);
      if (sectionRoot.classList && sectionRoot.classList.contains('shopify-section')) {
        sectionRoot.classList.add('oddit-section-active');
      }
      sectionRoot.querySelectorAll('.oddit-reveal').forEach(function (el) {
        el.classList.add('oddit-is-visible');
      });
      return;
    }

    initStagger(sectionRoot);
    if (sectionRoot.classList && sectionRoot.classList.contains('shopify-section')) {
      observeShopifySection(sectionRoot);
    }
    sectionRoot.querySelectorAll('.oddit-reveal').forEach(function (el) {
      observeReveal(el);
    });
    requestAnimationFrame(function () {
      flushInitiallyVisibleReveals(sectionRoot);
    });
  }

  function refreshAllThemeEditorInteractions() {
    if (!isThemeEditor()) {
      return;
    }
    initStagger();
    document.querySelectorAll('.shopify-section').forEach(function (el) {
      el.classList.add('oddit-section-active');
    });
    document.querySelectorAll('.oddit-reveal').forEach(function (el) {
      el.classList.add('oddit-is-visible');
    });
  }

  function init() {
    initStagger();
    initPopoverScrollLock();

    if (isThemeEditor()) {
      refreshAllThemeEditorInteractions();
      initParallax();
      return;
    }

    initShopifySections();
    initReveals();
    requestAnimationFrame(function () {
      flushInitiallyVisibleReveals(document);
    });
    initParallax();
  }

  function onShopifySectionLoad(e) {
    if (e && e.target) {
      attachInteractionsToSectionRoot(e.target);
      initPopoverScrollLock(e.target);
    }
    if (requestParallaxUpdate) {
      requestParallaxUpdate();
    } else {
      initParallax();
    }
  }

  document.addEventListener('shopify:section:load', onShopifySectionLoad);
  document.addEventListener('shopify:section:select', function (e) {
    if (e && e.target) {
      attachInteractionsToSectionRoot(e.target);
    }
    if (requestParallaxUpdate) {
      requestParallaxUpdate();
    }
  });
  document.addEventListener('shopify:block:select', function (e) {
    var section =
      e &&
      e.target &&
      e.target.closest &&
      e.target.closest('.shopify-section');
    if (section) {
      attachInteractionsToSectionRoot(section);
    }
    if (requestParallaxUpdate) {
      requestParallaxUpdate();
    }
  });
  document.addEventListener('shopify:editor:load', function () {
    refreshAllThemeEditorInteractions();
    if (requestParallaxUpdate) {
      requestParallaxUpdate();
    }
  });

  reducedMotion.addEventListener('change', function (e) {
    if (e.matches) {
      document.querySelectorAll('.shopify-section').forEach(function (el) {
        el.classList.add('oddit-section-active');
      });
      document.querySelectorAll('.oddit-reveal').forEach(function (el) {
        el.classList.add('oddit-is-visible');
      });
      if (sectionShellObserver) {
        sectionShellObserver.disconnect();
        sectionShellObserver = null;
      }
      if (revealObserver) {
        revealObserver.disconnect();
        revealObserver = null;
      }
    } else {
      if (staticPreview) {
        return;
      }
      if (isThemeEditor()) {
        refreshAllThemeEditorInteractions();
      } else {
        initShopifySections();
        document.querySelectorAll('.oddit-reveal:not(.oddit-is-visible)').forEach(function (el) {
          observeReveal(el);
        });
      }
      initParallax();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
